import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Search, Grid, List, CheckCircle, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { publicationAPI } from '../services/api';
import { Publication } from '../types';
import PublicationCard from '../components/Publications/PublicationCard';
import PublicationListItem from '../components/Publications/PublicationListItem';
import logoImage from '../images/image.png';

const TotalPapersDashboard: React.FC = () => {
  const { user } = useAuth();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [filteredPublications, setFilteredPublications] = useState<Publication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedStage, setSelectedStage] = useState<string>('all');

  useEffect(() => {
    loadPublications();
  }, []);

  const loadPublications = async () => {
    try {
      setIsLoading(true);
      const data = await publicationAPI.getPublications();
      setPublications(data);
      setFilteredPublications(data);
    } catch (error) {
      console.error('Error loading publications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    filterPublications();
  }, [searchTerm, publications, selectedStage]);

  const filterPublications = () => {
    let filtered = [...publications];

    // Filter by stage
    if (selectedStage !== 'all') {
      filtered = filtered.filter(p => p.stage === selectedStage);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.abstract.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.leadAuthorName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPublications(filtered);
  };

  const getStageText = (stage: string) => {
    switch (stage) {
      case '1': return 'Ideation';
      case '2': return 'Review & Team';
      case '3': return 'Data Analysis';
      case '4': return 'Manuscript';
      case 'published': return 'Published';
      default: return 'Unknown';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'all': return 'bg-gray-100 text-gray-800 border-gray-200';
      case '1': return 'bg-blue-100 text-blue-800 border-blue-200';
      case '2': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case '3': return 'bg-purple-100 text-purple-800 border-purple-200';
      case '4': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'published': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStageStats = () => {
    const stats = {
      total: publications.length,
      inProgress: publications.filter(p => ['1', '2', '3', '4'].includes(p.stage)).length,
      published: publications.filter(p => p.stage === 'published').length,
      stage1: publications.filter(p => p.stage === '1').length,
      stage2: publications.filter(p => p.stage === '2').length,
      stage3: publications.filter(p => p.stage === '3').length,
      stage4: publications.filter(p => p.stage === '4').length,
    };
    return stats;
  };

  const stats = getStageStats();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logoImage} 
            alt="Publication Tracker Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading all publications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Link 
                to="/dashboard" 
                className="mr-4 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200"
              >
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <h1 className="text-4xl font-bold">All Publications</h1>
            </div>
            <p className="text-xl text-green-100 mb-8">
              Complete overview of all research publications
            </p>
            
            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
                <FileText className="h-8 w-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-green-100">Total Papers</div>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
                <TrendingUp className="h-8 w-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">{stats.inProgress}</div>
                <div className="text-sm text-green-100">In Progress</div>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
                <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                <div className="text-2xl font-bold">{stats.published}</div>
                <div className="text-sm text-green-100">Published</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and View Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6 items-start justify-between">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-green-600" />
              All Publications
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search all papers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent w-64"
                />
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white text-green-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white text-green-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stage Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter by Stage</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {[
              { stage: 'all', title: 'All', count: stats.total },
              { stage: '1', title: 'Ideation', count: stats.stage1 },
              { stage: '2', title: 'Review & Team', count: stats.stage2 },
              { stage: '3', title: 'Data Analysis', count: stats.stage3 },
              { stage: '4', title: 'Manuscript', count: stats.stage4 },
              { stage: 'published', title: 'Published', count: stats.published },
            ].map((item) => (
              <button
                key={item.stage}
                onClick={() => setSelectedStage(item.stage)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                  selectedStage === item.stage
                    ? `${getStageColor(item.stage)} border-current`
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="text-2xl font-bold">{item.count}</div>
                <div className="text-sm">{item.title}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Publications Display */}
        {filteredPublications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || selectedStage !== 'all' ? 'No publications found' : 'No publications yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedStage !== 'all'
                ? 'Try adjusting your search terms or filters' 
                : 'Publications will appear here once they are submitted'
              }
            </p>
            {!searchTerm && selectedStage === 'all' && (
              <Link
                to="/publications"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredPublications.length} of {publications.length} publications
                {selectedStage !== 'all' && ` in ${getStageText(selectedStage)} stage`}
              </p>
            </div>

            {/* Publications Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPublications.map((publication) => (
                  <PublicationCard
                    key={publication.id}
                    publication={publication}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stage
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Lead Author
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Authors
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPublications.map((publication) => (
                        <PublicationListItem
                          key={publication.id}
                          publication={publication}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TotalPapersDashboard;
